const MONTH_NAMES_ABBR = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const MONTH_DAYS = 42;
const DEFAULT_LOAD_MORE_LIMIT = 6;

// Constants for filter and sort values
const SORT_VALUES = {
  NEWEST: "newest",
  OLDEST: "oldest",
};

const PILL_VALUES = {
  TODAY: "today",
  THIS_WEEK: "this-week",
  THIS_MONTH: "this-month",
  PAST: "past",
};

const DATE_INPUT_TYPES = {
  FROM: "from",
  TO: "to",
};

const CALENDAR_CLASSES = {
  DAY: "date-range-calendar__day",
  DAY_TODAY: "date-range-calendar__day--today",
  DAY_SELECTED_FROM: "date-range-calendar__day--selected-from",
  DAY_SELECTED_TO: "date-range-calendar__day--selected-to",
  DAY_IN_RANGE: "date-range-calendar__day--in-range",
  CALENDAR_FROM: "date-range-calendar--from",
  CALENDAR_TO: "date-range-calendar--to",
};

const ARTICLE_CLASSES = {
  ARTICLE: "new-blog-posts__article",
  HIDDEN_BY_LOAD_MORE: "hidden-by-load-more",
};

const FILTER_CLASSES = {
  PILL: "filter-pill",
  ACTIVE: "active",
  TIMING_PILL: "timing-pill",
};

const MONTH_TYPES = {
  PREV: "prev-month",
  CURRENT: "current-month",
  NEXT: "next-month",
};

const EVENT_SECTIONS = {
  UPCOMING: "upcoming",
  PAST: "past",
};

// Utility function to convert kebab-case to camelCase
const toCamelCase = (str) =>
  str.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());

// Date formatting helpers
const DateFormatters = {
  // Format date as "30 Dec 2024"
  formatDateShort(date) {
    const day = date.getDate();
    const month = MONTH_NAMES_ABBR[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  },

  // Format date as "30 December 2024"
  formatDateLong(date) {
    const day = date.getDate();
    const month = MONTH_NAMES[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  },

  // Format time as "14:30" (24-hour)
  formatTime24(date) {
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  },

  // Format time as "2:30PM" (12-hour with AM/PM)
  formatTime12(date) {
    const hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const period = hours >= 12 ? "PM" : "AM";
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes}${period}`;
  },
};

// Blog posts filtering functionality
(function () {
  // Date range calendar class
  class DateRangeCalendar {
    constructor(container) {
      this.container = container;
      this.calendar = container.querySelector("[data-calendar]");
      this.fromInput = container.querySelector('[data-date-input="from"]');
      this.toInput = container.querySelector('[data-date-input="to"]');
      this.monthDisplay = this.calendar.querySelector("[data-calendar-month]");
      this.daysContainer = this.calendar.querySelector("[data-calendar-days]");
      this.prevBtn = this.calendar.querySelector("[data-calendar-prev]");
      this.nextBtn = this.calendar.querySelector("[data-calendar-next]");

      this.currentDate = new Date();
      this.selectedFrom = null;
      this.selectedTo = null;
      this.isSelectingRange = false; // Track if we're in range selection mode

      this.init();
    }

    init() {
      // Open calendar on either input click
      this.fromInput.addEventListener("click", () => this.openCalendar());
      this.toInput.addEventListener("click", () => this.openCalendar());

      // Navigation
      this.prevBtn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.changeMonth(-1);
      });
      this.nextBtn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.changeMonth(1);
      });

      // Close on outside click
      document.addEventListener("click", (e) => {
        if (!this.container.contains(e.target)) {
          this.closeCalendar();
        }
      });

      this.renderCalendar();
    }

    openCalendar() {
      this.calendar.hidden = false;
      this.isSelectingRange = true;

      // Position calendar based on available space
      // If clicking 'to' input and space is limited on left, align right
      const rect = this.container.getBoundingClientRect();
      const spaceOnRight = window.innerWidth - rect.right;

      if (spaceOnRight < 300) {
        this.calendar.classList.add(CALENDAR_CLASSES.CALENDAR_TO);
        this.calendar.classList.remove(CALENDAR_CLASSES.CALENDAR_FROM);
      } else {
        this.calendar.classList.add(CALENDAR_CLASSES.CALENDAR_FROM);
        this.calendar.classList.remove(CALENDAR_CLASSES.CALENDAR_TO);
      }

      this.renderCalendar();
    }

    closeCalendar() {
      this.calendar.hidden = true;
      this.isSelectingRange = false;
    }

    changeMonth(delta) {
      this.currentDate.setMonth(this.currentDate.getMonth() + delta);
      this.renderCalendar();
    }

    renderCalendar() {
      const year = this.currentDate.getFullYear();
      const month = this.currentDate.getMonth();

      // Update month display
      this.monthDisplay.textContent = `${MONTH_NAMES[month]} ${year}`;

      // Calculate days
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const startDay = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1; // Monday = 0
      const daysInMonth = lastDay.getDate();

      // Clear days
      this.daysContainer.innerHTML = "";

      // Previous month days
      const prevMonthLastDay = new Date(year, month, 0).getDate();
      for (let i = startDay - 1; i >= 0; i--) {
        this.createDayElement(prevMonthLastDay - i, MONTH_TYPES.PREV);
      }

      // Current month days
      for (let day = 1; day <= daysInMonth; day++) {
        this.createDayElement(day, MONTH_TYPES.CURRENT, year, month);
      }

      // Next month days
      const remainingDays = MONTH_DAYS - (startDay + daysInMonth); // 6 rows * 7 days
      for (let day = 1; day <= remainingDays; day++) {
        this.createDayElement(day, MONTH_TYPES.NEXT);
      }
    }

    createDayElement(day, type, year, month) {
      const dayEl = document.createElement("button");
      dayEl.className = `${CALENDAR_CLASSES.DAY} ${CALENDAR_CLASSES.DAY}--${type}`;
      dayEl.textContent = day;
      dayEl.type = "button";

      if (type === MONTH_TYPES.CURRENT) {
        const date = new Date(year, month, day);
        const dateStr = this.formatDate(date);
        const today = new Date();
        const todayStr = this.formatDate(today);

        // Check if today
        if (dateStr === todayStr) {
          dayEl.classList.add(CALENDAR_CLASSES.DAY_TODAY);
        }

        // Check if selected
        if (
          this.selectedFrom &&
          dateStr === this.formatDate(this.selectedFrom)
        ) {
          dayEl.classList.add(CALENDAR_CLASSES.DAY_SELECTED_FROM);
        }
        if (this.selectedTo && dateStr === this.formatDate(this.selectedTo)) {
          dayEl.classList.add(CALENDAR_CLASSES.DAY_SELECTED_TO);
        }

        // Check if in range
        if (
          this.selectedFrom &&
          this.selectedTo &&
          date > this.selectedFrom &&
          date < this.selectedTo
        ) {
          dayEl.classList.add(CALENDAR_CLASSES.DAY_IN_RANGE);
        }

        // Add hover preview when selecting end date
        if (this.selectedFrom && !this.selectedTo && this.isSelectingRange) {
          dayEl.addEventListener("mouseenter", () => {
            this.previewRange(date);
          });

          dayEl.addEventListener("mouseleave", () => {
            this.clearRangePreview();
          });
        }

        dayEl.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.selectDate(date);
        });
      } else {
        dayEl.disabled = true;
      }

      this.daysContainer.appendChild(dayEl);
    }

    previewRange(hoverDate) {
      if (!this.selectedFrom || this.selectedTo) return;

      const days = this.daysContainer.querySelectorAll(
        `.${CALENDAR_CLASSES.DAY}:not(.${CALENDAR_CLASSES.DAY}--prev-month):not(.${CALENDAR_CLASSES.DAY}--next-month)`,
      );

      days.forEach((dayEl) => {
        const dayText = dayEl.textContent;
        const year = this.currentDate.getFullYear();
        const month = this.currentDate.getMonth();
        const date = new Date(year, month, parseInt(dayText));

        // Add preview class to dates in range
        if (
          (date > this.selectedFrom && date < hoverDate) ||
          (date > hoverDate && date < this.selectedFrom)
        ) {
          dayEl.classList.add("date-range-calendar__day--preview");
        }

        // Highlight the hovered date as potential end date
        if (this.formatDate(date) === this.formatDate(hoverDate)) {
          dayEl.classList.add("date-range-calendar__day--preview-end");
        }
      });
    }

    clearRangePreview() {
      const previewDays = this.daysContainer.querySelectorAll(
        ".date-range-calendar__day--preview, .date-range-calendar__day--preview-end",
      );
      previewDays.forEach((day) => {
        day.classList.remove("date-range-calendar__day--preview");
        day.classList.remove("date-range-calendar__day--preview-end");
      });
    }

    selectDate(date) {
      // If no dates selected, or both dates already selected, start new range
      if (!this.selectedFrom || (this.selectedFrom && this.selectedTo)) {
        this.selectedFrom = date;
        this.selectedTo = null;
        this.fromInput.value = this.formatDateDisplay(date);
        this.toInput.value = "";

        // Trigger filter updates for both inputs to clear old state
        this.fromInput.dispatchEvent(new Event("change", { bubbles: true }));
        this.toInput.dispatchEvent(new Event("change", { bubbles: true }));

        // Keep calendar open for selecting end date
        this.renderCalendar();
      } else {
        // We have a start date, now selecting end date
        if (date < this.selectedFrom) {
          // If selected date is before start, swap them
          this.selectedTo = this.selectedFrom;
          this.selectedFrom = date;
        } else {
          this.selectedTo = date;
        }

        // Update both inputs
        this.fromInput.value = this.formatDateDisplay(this.selectedFrom);
        this.toInput.value = this.formatDateDisplay(this.selectedTo);

        // Trigger filter updates
        this.fromInput.dispatchEvent(new Event("change", { bubbles: true }));
        this.toInput.dispatchEvent(new Event("change", { bubbles: true }));

        // Close calendar after both dates are selected
        this.renderCalendar();
        this.closeCalendar();
      }
    }

    formatDate(date) {
      return date.toISOString().split("T")[0];
    }

    formatDateDisplay(date) {
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }

    restoreState(fromValue, toValue) {
      if (fromValue) {
        this.selectedFrom = parseDateInput(fromValue);
      }
      if (toValue) {
        this.selectedTo = parseDateInput(toValue);
      }
      this.renderCalendar();
    }
  }

  // Parse date input in dd/mm/yyyy format (moved to be accessible by DateRangeCalendar)
  function parseDateInput(dateStr) {
    const [day, month, year] = dateStr.split("/");
    return new Date(year, month - 1, day);
  }

  // Initialize date range pickers
  function initDateRangePickers() {
    const pickers = new Map();
    document
      .querySelectorAll("[data-date-range-picker]")
      .forEach((container) => {
        const picker = new DateRangeCalendar(container);
        pickers.set(container, picker);
      });
    return pickers;
  }

  // Store pickers globally for state restoration
  let dateRangePickers = new Map();

  // Convert event dates to user's local timezone
  function convertEventDatesToLocal() {
    // Handle datetime containers (for multi-day events)
    document
      .querySelectorAll(".pd-event-card__datetime")
      .forEach((container) => {
        const isMultiday = container.dataset.isMultiday === "true";

        if (isMultiday) {
          const startTimestamp = container.dataset.start;
          const endTimestamp = container.dataset.end;

          if (startTimestamp && endTimestamp) {
            const startDate = new Date(startTimestamp);
            const endDate = new Date(endTimestamp);

            // Format: "10 Dec 2025, 10:00AM – 11 Dec 2025, 5:00PM"
            const startDateEl = container.querySelector(
              ".pd-event-card__start-date",
            );
            const startTimeEl = container.querySelector(
              ".pd-event-card__start-time",
            );
            const endDateEl = container.querySelector(
              ".pd-event-card__end-date",
            );
            const endTimeEl = container.querySelector(
              ".pd-event-card__end-time",
            );

            if (startDateEl) {
              startDateEl.textContent =
                DateFormatters.formatDateLong(startDate);
            }

            if (startTimeEl) {
              startTimeEl.textContent = DateFormatters.formatTime12(startDate);
            }

            if (endDateEl) {
              endDateEl.textContent = DateFormatters.formatDateShort(endDate);
            }

            if (endTimeEl) {
              endTimeEl.textContent = DateFormatters.formatTime12(endDate);
            }
          }
        }
      });

    // Handle individual timestamp elements (for single-day events)
    document.querySelectorAll("[data-timestamp]").forEach((el) => {
      const timestamp = el.getAttribute("data-timestamp");
      if (!timestamp) return;

      const date = new Date(timestamp);

      const shortDateClasses = [
        "pd-event-card__date",
        "pd-event-card__start-date",
        "pd-event-details__end-date",
      ];

      if (shortDateClasses.some((cls) => el.classList.contains(cls))) {
        // "30 Dec 2024"
        el.textContent = DateFormatters.formatDateShort(date);
      } else if (
        el.classList.contains("pd-event-card__time") ||
        el.classList.contains("pd-event-details__time")
      ) {
        // "14:30"
        el.textContent = DateFormatters.formatTime24(date);
        //el.textContent = DateFormatters.formatTime12(date);
      }
    });
  }

  // URL parameter management
  function getUrlParams() {
    const params = new URLSearchParams(window.location.search);

    const filters = tryParseJSON(params.get("filters"));
    const dateRanges = tryParseJSON(params.get("dateRanges"));

    return {
      search: params.get("search") || "",
      sort: params.get("sort") || SORT_VALUES.NEWEST,
      pill: params.get("pill") || "",
      filters: filters,
      dateRanges: dateRanges,
    };
  }

  function updateUrlParams(params) {
    const url = new URL(window.location);

    // Update search param
    if (params.search) {
      url.searchParams.set("search", params.search);
    } else {
      url.searchParams.delete("search");
    }

    // Update sort param
    if (params.sort && params.sort !== SORT_VALUES.NEWEST) {
      url.searchParams.set("sort", params.sort);
    } else {
      url.searchParams.delete("sort");
    }

    // Update pill param
    if (params.pill) {
      url.searchParams.set("pill", params.pill);
    } else {
      url.searchParams.delete("pill");
    }

    // Update filters param
    if (Object.keys(params.filters).length > 0) {
      url.searchParams.set("filters", JSON.stringify(params.filters));
    } else {
      url.searchParams.delete("filters");
    }

    // Update date ranges param
    if (Object.keys(params.dateRanges).length > 0) {
      url.searchParams.set("dateRanges", JSON.stringify(params.dateRanges));
    } else {
      url.searchParams.delete("dateRanges");
    }

    // Update URL without reloading
    window.history.replaceState({}, "", url);
  }

  // Load more state management using WeakMap for automatic garbage collection
  const loadMoreState = new WeakMap();

  // Load more functionality for events
  function initLoadMore() {
    const sections = [EVENT_SECTIONS.UPCOMING, EVENT_SECTIONS.PAST];

    sections.forEach((section) => {
      const grid = document.querySelector(
        `[data-articles][data-section="${section}"]`,
      );
      const loadMoreBtn = document.querySelector(
        `[data-load-more-btn="${section}"]`,
      );
      const loadMoreContainer = document.querySelector(
        `[data-load-more-container="${section}"]`,
      );

      if (!grid || !loadMoreBtn || !loadMoreContainer) return;

      const limit =
        Number.parseInt(grid.dataset.loadMoreLimit, 10) ||
        DEFAULT_LOAD_MORE_LIMIT;

      // Initialize state for this grid element (WeakMap uses grid as key)
      loadMoreState.set(grid, {
        loadMoreContainer,
        limit,
        currentlyShown: limit,
      });

      // Function to update visibility
      function updateVisibility() {
        const state = loadMoreState.get(grid);
        if (!state) return;

        const articles = [
          ...grid.querySelectorAll(`.${ARTICLE_CLASSES.ARTICLE}`),
        ];
        const visibleArticles = articles.filter(
          (article) => article.style.display !== "none",
        );

        // Hide articles beyond the limit
        visibleArticles.forEach((article, index) => {
          if (index >= state.currentlyShown) {
            article.classList.add(ARTICLE_CLASSES.HIDDEN_BY_LOAD_MORE);
          } else {
            article.classList.remove(ARTICLE_CLASSES.HIDDEN_BY_LOAD_MORE);
          }
        });

        // Show/hide load more button
        const shouldLoadMoreHidden =
          state.currentlyShown >= visibleArticles.length;
        state.loadMoreContainer.hidden = shouldLoadMoreHidden;
      }

      // Load more button click handler
      loadMoreBtn.addEventListener("click", () => {
        const state = loadMoreState.get(grid);
        if (state) {
          state.currentlyShown += state.limit;
          updateVisibility();
        }
      });

      // Initial update
      updateVisibility();
    });
  }

  // Reset load more state for a specific grid
  function resetLoadMoreForGrid(grid) {
    const state = loadMoreState.get(grid);
    if (!state) return;

    state.currentlyShown = state.limit;

    // Update visibility
    const articles = [...grid.querySelectorAll(`.${ARTICLE_CLASSES.ARTICLE}`)];
    const visibleArticles = articles.filter(
      (article) => article.style.display !== "none",
    );

    visibleArticles.forEach((article, index) => {
      if (index >= state.currentlyShown) {
        article.classList.add(ARTICLE_CLASSES.HIDDEN_BY_LOAD_MORE);
      } else {
        article.classList.remove(ARTICLE_CLASSES.HIDDEN_BY_LOAD_MORE);
      }
    });

    // Show/hide load more button
    if (visibleArticles.length > state.currentlyShown) {
      state.loadMoreContainer.hidden = false;
    } else {
      state.loadMoreContainer.hidden = true;
    }
  }

  // Compare dates function - returns positive if a > b, negative if a < b
  // date format: 2025-01-06T10:00:00.000Z
  function compareDates(a, z) {
    return a.dataset.dateUnix - z.dataset.dateUnix;
  }

  // Sort comparators
  const newestFirst = (a, z) => compareDates(z, a);
  const oldestFirst = (a, z) => compareDates(a, z);

  // Sort articles in the DOM
  function sortArticles(sortValue) {
    const lists = document.querySelectorAll("[data-articles]");
    if (!lists.length) return;

    // Determine sort comparator once
    const sortComparator =
      sortValue === SORT_VALUES.NEWEST ? newestFirst : oldestFirst;

    // Sort articles in each list (for events with multiple sections)
    lists.forEach((list) => {
      const listArticles = [
        ...list.querySelectorAll(`.${ARTICLE_CLASSES.ARTICLE}`),
      ];

      // Sort using the selected comparator
      listArticles.sort(sortComparator);

      // Use document fragment for better performance
      const fragment = document.createDocumentFragment();
      listArticles.forEach((el) => fragment.appendChild(el));
      list.appendChild(fragment);
    });
  }

  // Update URL with sort parameter
  function updateSortInUrl(sortValue) {
    const currentParams = getUrlParams();
    updateUrlParams({
      ...currentParams,
      sort: sortValue,
    });
  }

  // Handle Shopify section rendering (if applicable)
  document.addEventListener("shopify:section:load", () => {
    // Reinitialize load more when sections are dynamically loaded
    initLoadMore();
  });

  document.addEventListener("DOMContentLoaded", () => {
    // Convert event dates to local timezone
    convertEventDatesToLocal();

    // Initialize date range pickers
    dateRangePickers = initDateRangePickers();

    // Initialize load more for events
    initLoadMore();

    const searchInput = document.querySelector("[data-search]");
    const sortSelect = document.querySelector("[data-sort]");
    const sortSelectMobile = document.querySelector("[data-sort-mobile]");
    const filterInputs = document.querySelectorAll("[data-filter-type]");
    const pillButtons = document.querySelectorAll("[data-filter-pill]");
    const articles = [
      ...document.querySelectorAll(`.${ARTICLE_CLASSES.ARTICLE}`),
    ];

    if (!articles.length) return;

    // Restore state from URL parameters
    const urlParams = getUrlParams();

    // Restore search
    if (searchInput && urlParams.search) {
      searchInput.value = urlParams.search;
    }

    // Restore sort
    if (urlParams.sort) {
      if (sortSelect) sortSelect.value = urlParams.sort;
      if (sortSelectMobile) sortSelectMobile.value = urlParams.sort;
      sortArticles(urlParams.sort);
    }

    // Restore pill filter
    if (urlParams.pill) {
      const pillBtn = document.querySelector(
        `[data-filter-pill][data-pill-value="${urlParams.pill}"]`,
      );
      if (pillBtn) pillBtn.classList.add(FILTER_CLASSES.ACTIVE);
    }

    // Restore accordion filters
    if (Object.keys(urlParams.filters).length > 0) {
      filterInputs.forEach((input) => {
        const group = input.dataset.filterAttribute;
        const value = input.value.toLowerCase();
        if (
          urlParams.filters[group] &&
          urlParams.filters[group].includes(value)
        ) {
          input.checked = true;
        }
      });
    }

    // Restore date range filters
    if (Object.keys(urlParams.dateRanges).length > 0) {
      document
        .querySelectorAll("[data-date-range-picker]")
        .forEach((pickerContainer) => {
          const attr = pickerContainer.dataset.filterAttribute;
          if (urlParams.dateRanges[attr]) {
            const fromInput = pickerContainer.querySelector(
              '[data-date-input="from"]',
            );
            const toInput = pickerContainer.querySelector(
              '[data-date-input="to"]',
            );

            // Set input values
            if (fromInput && urlParams.dateRanges[attr].from) {
              fromInput.value = urlParams.dateRanges[attr].from;
            }
            if (toInput && urlParams.dateRanges[attr].to) {
              toInput.value = urlParams.dateRanges[attr].to;
            }

            // Restore picker internal state
            const picker = dateRangePickers.get(pickerContainer);
            if (picker) {
              picker.restoreState(
                urlParams.dateRanges[attr].from,
                urlParams.dateRanges[attr].to,
              );
            }
          }
        });
    }

    // Apply filters after restoration (without updating URL)
    applyFiltersToDOM();
    updateResultCount();
    renderActiveFilters(true);
    updateLoadMoreVisibility();
    updateMobileFilterCount(true);

    // Collect current filter state from DOM
    function collectFilterState() {
      const search = searchInput?.value.toLowerCase() || "";

      // Get checked accordion filters grouped by filter attribute
      const accordionFilters = {};
      filterInputs.forEach((input) => {
        if (input.checked) {
          const group = input.dataset.filterAttribute;
          if (!accordionFilters[group]) accordionFilters[group] = [];
          accordionFilters[group].push(input.value.toLowerCase());
        }
      });

      // Get active pill filters
      const activePill = document.querySelector(
        `[data-filter-pill].${FILTER_CLASSES.ACTIVE}`,
      );
      const pillValue = activePill?.dataset.pillValue;

      // Get date range filters
      const dateRangeFilters = {};
      const dateRangeFiltersForUrl = {};
      document
        .querySelectorAll("[data-date-range-picker]")
        .forEach((picker) => {
          const hiddenDateRangeCalendar = picker.querySelector(
            "[data-calendar][hidden]",
          );
          if (hiddenDateRangeCalendar) return;
          const fromInput = picker.querySelector('[data-date-input="from"]');
          const toInput = picker.querySelector('[data-date-input="to"]');

          if (fromInput.value || toInput.value) {
            const attr = picker.dataset.filterAttribute;
            dateRangeFilters[attr] = {
              from: fromInput.value ? parseDateInput(fromInput.value) : null,
              to: toInput.value ? parseDateInput(toInput.value) : null,
            };
            dateRangeFiltersForUrl[attr] = {
              from: fromInput.value || "",
              to: toInput.value || "",
            };
          }
        });

      return {
        search,
        accordionFilters,
        pillValue,
        dateRangeFilters,
        dateRangeFiltersForUrl,
      };
    }

    function updateMobileFilterCount(fromUrlParams = false) {
      const countEl = document.querySelector("[data-filter-count]");
      if (!countEl) return;

      let count = 0;

      if (fromUrlParams) {
        const { filters, pill, dateRanges } = getUrlParams();

        // 1. Accordion filters from URL
        if (filters) {
          Object.values(filters).forEach((values) => {
            count += values.length;
          });
        }

        // 2. Pill filter
        if (pill) {
          count += 1;
        }

        // 3. Date ranges
        if (dateRanges) {
          Object.values(dateRanges).forEach((range) => {
            if (range.from || range.to) {
              count += 1;
            }
          });
        }
      } else {
        const { accordionFilters, pillValue, dateRangeFilters } =
          collectFilterState();

        Object.values(accordionFilters).forEach((values) => {
          count += values.length;
        });

        if (pillValue) {
          count += 1;
        }

        Object.values(dateRangeFilters).forEach((range) => {
          if (range.from || range.to) {
            count += 1;
          }
        });
      }

      if (count > 0) {
        countEl.textContent = `(${count})`;
        countEl.hidden = false;
      } else {
        countEl.hidden = true;
      }
    }

    // Apply filters to DOM (pure DOM manipulation, no side effects)
    function applyFiltersToDOM() {
      const { search, accordionFilters, pillValue, dateRangeFilters } =
        collectFilterState();

      // Track visible count per section
      const visibleCountBySection = {};

      articles.forEach((article) => {
        const title = article.dataset.title?.toLowerCase() || "";

        // Check search match
        const matchesSearch = title.includes(search);

        // Check accordion filters (all groups must match)
        let matchesAccordion = true;
        for (const [attr, values] of Object.entries(accordionFilters)) {
          const camelCaseAttr = toCamelCase(attr);
          const articleValue =
            article.dataset[camelCaseAttr]?.toLowerCase() || "";
          if (!values.some((v) => articleValue.includes(v))) {
            matchesAccordion = false;
            break;
          }
        }

        // Check pill filter (date-based)
        let matchesPill = true;
        if (pillValue) {
          matchesPill = matchesDateFilter(article, pillValue);
        }

        // Check date range filters
        let matchesDateRange = true;
        for (const [attr, range] of Object.entries(dateRangeFilters)) {
          const camelCaseAttr = toCamelCase(attr);
          const articleDateStr = article.dataset[camelCaseAttr];

          if (!articleDateStr) {
            matchesDateRange = false;
            break;
          }

          const articleDate = new Date(articleDateStr);

          if (range.from && articleDate < range.from) {
            matchesDateRange = false;
            break;
          }
          if (range.to && articleDate > range.to) {
            matchesDateRange = false;
            break;
          }
        }

        const isVisible =
          matchesSearch && matchesAccordion && matchesPill && matchesDateRange;

        article.style.display = isVisible ? "" : "none";

        // Track visible count by section
        if (isVisible) {
          const section = article.closest("[data-articles]")?.dataset.section;
          if (section) {
            visibleCountBySection[section] =
              (visibleCountBySection[section] || 0) + 1;
          }
        }
      });

      // Show/hide no results message per section
      document.querySelectorAll("[data-articles]").forEach((grid) => {
        const section = grid.dataset.section;
        const noResultsEl = grid.querySelector("[data-no-results-text]");

        if (noResultsEl) {
          const visibleCount = visibleCountBySection[section] || 0;
          if (visibleCount === 0) {
            noResultsEl.classList.remove("hidden");
          } else {
            noResultsEl.classList.add("hidden");
          }
        }
      });
    }

    // Update URL with current filter state
    function syncUrlWithFilters() {
      const { accordionFilters, pillValue, dateRangeFiltersForUrl } =
        collectFilterState();

      updateUrlParams({
        search: searchInput?.value || "",
        sort: sortSelect?.value || SORT_VALUES.NEWEST,
        pill: pillValue || "",
        filters: accordionFilters,
        dateRanges: dateRangeFiltersForUrl,
      });
    }

    // Main filter function: applies filters and updates URL
    function filterArticles() {
      applyFiltersToDOM();
      updateResultCount();
      renderActiveFilters();
      updateLoadMoreVisibility();
      syncUrlWithFilters();
      updateMobileFilterCount();
    }

    // Update load more visibility
    function updateLoadMoreVisibility() {
      document.querySelectorAll("[data-articles]").forEach((grid) => {
        if (loadMoreState.has(grid)) {
          resetLoadMoreForGrid(grid);
        }
      });
    }

    // Date filtering logic for pills
    function matchesDateFilter(article, pillValue) {
      const articleDate = new Date(article.dataset.date);
      const now = new Date();

      switch (pillValue) {
        case PILL_VALUES.TODAY:
          return isSameDay(articleDate, now);
        case PILL_VALUES.THIS_WEEK:
          return isThisWeek(articleDate, now);
        case PILL_VALUES.THIS_MONTH:
          return isSameMonth(articleDate, now);
        case PILL_VALUES.PAST:
          return articleDate < now;
        default:
          return true;
      }
    }

    // Date helper functions
    function isSameDay(d1, d2) {
      return d1.toDateString() === d2.toDateString();
    }

    function isThisWeek(date, now) {
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() - now.getDay());
      weekStart.setHours(0, 0, 0, 0);

      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      weekEnd.setHours(23, 59, 59, 999);

      return date >= weekStart && date <= weekEnd;
    }

    function isSameMonth(d1, d2) {
      return (
        d1.getMonth() === d2.getMonth() && d1.getFullYear() === d2.getFullYear()
      );
    }

    // Update result count
    function updateResultCount() {
      const resultCountEl = document.querySelector("[data-result-count]");
      const labelEl = document.querySelector("[data-result-label]");
      if (!resultCountEl || !labelEl) return;

      const visibleCount = articles.filter(
        (article) => article.style.display !== "none",
      ).length;

      resultCountEl.textContent = visibleCount;
      labelEl.textContent = visibleCount === 1 ? "item" : "items";
    }

    // Render active filter pills
    function renderActiveFilters(isRestoredFromUrl = false) {
      const { dateRangeFilters } = collectFilterState();

      const mediaQuery = window.matchMedia("(max-width: 989px)");

      let activeFiltersEls;

      if (mediaQuery.matches) {
        // tablet & down
        activeFiltersEls = document.querySelectorAll(
          ".new-blog-posts__drawer [data-active-filters]",
        );
      } else {
        // desktop
        activeFiltersEls = document.querySelectorAll(
          "[data-active-filters].hide-tablet-and-down",
        );
      }

      activeFiltersEls.forEach((el) => {
        el.innerHTML = "";
      });

      // Collect all active filters first
      const activePills = [];

      // Accordion filter pills
      filterInputs.forEach((input) => {
        if (!input.checked) return;

        const group = input.dataset.filterGroup;
        const value = input.value;

        activePills.push({
          group,
          value,
          onRemove: () => {
            input.checked = false;
            filterArticles();
          },
        });
      });

      // Pill filter active indicator
      const activePillBtn = document.querySelector(
        `[data-filter-pill].${FILTER_CLASSES.ACTIVE}`,
      );
      if (activePillBtn) {
        activePills.push({
          group: "Date",
          value: activePillBtn.textContent.trim(),
          onRemove: () => {
            activePillBtn.classList.remove(FILTER_CLASSES.ACTIVE);
            filterArticles();
          },
        });
      }

      // Date range filter pills
      document
        .querySelectorAll("[data-date-range-picker]")
        .forEach((picker) => {
          const fromInput = picker.querySelector('[data-date-input="from"]');
          const toInput = picker.querySelector('[data-date-input="to"]');
          const group = fromInput?.dataset.filterGroup || "Date Range";

          if (fromInput.value || toInput.value) {
            const rangeText = `${fromInput.value || "..."} - ${toInput.value || "..."}`;

            activePills.push({
              group,
              value: rangeText,
              onRemove: () => {
                fromInput.value = "";
                toInput.value = "";
                filterArticles();
              },
            });
          }
        });

      const uniquePills = activePills.filter(
        (pill, index, self) =>
          index ===
          self.findIndex(
            (p) => p.group === pill.group && p.value === pill.value,
          ),
      );

      const validPills = uniquePills.filter((pill) => {
        if (isRestoredFromUrl) {
          return true;
        }

        if (pill.group === "Timing") {
          // Parse the pill value to extract from and to dates
          const rangeParts = pill.value.split(" - ");
          const pillFrom =
            rangeParts[0] !== "..." ? parseDateInput(rangeParts[0]) : null;
          const pillTo =
            rangeParts[1] !== "..." ? parseDateInput(rangeParts[1]) : null;

          // Check if there are date range filters for timing
          if (dateRangeFilters.timing) {
            const filterFrom = dateRangeFilters.timing.from;
            const filterTo = dateRangeFilters.timing.to;

            // Compare dates - pill should match the current filter
            const fromMatches =
              (!pillFrom && !filterFrom) ||
              (pillFrom &&
                filterFrom &&
                pillFrom.getTime() === filterFrom.getTime());
            const toMatches =
              (!pillTo && !filterTo) ||
              (pillTo && filterTo && pillTo.getTime() === filterTo.getTime());

            return fromMatches && toMatches;
          }

          // If no timing filter is set, don't show the timing pill
          return false;
        }
        return true;
      });

      activeFiltersEls.forEach((container) => {
        validPills.forEach((pillData) => {
          const pill = createFilterPill(
            pillData.group,
            pillData.value,
            pillData.onRemove,
          );
          container.appendChild(pill);
        });
      });
    }

    // Create filter pill element
    function createFilterPill(group, value, onRemove) {
      const pill = document.createElement("div");
      pill.className = FILTER_CLASSES.PILL;

      if (group === "Timing") {
        pill.classList.add(FILTER_CLASSES.TIMING_PILL);
      }

      // Create group label
      const groupSpan = document.createElement("span");
      groupSpan.textContent = `${group}:`;

      // Create value label
      const valueSpan = document.createElement("span");
      valueSpan.textContent = value;

      // Create remove button
      const button = document.createElement("button");
      button.setAttribute("aria-label", "Remove filter");
      button.addEventListener("click", onRemove);

      // Create SVG wrapper
      const svgWrapper = document.createElement("span");
      svgWrapper.className = "svg-wrapper";

      // Create SVG element
      const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      svg.setAttribute("width", "14");
      svg.setAttribute("height", "14");
      svg.setAttribute("viewBox", "0 0 14 14");
      svg.setAttribute("fill", "none");

      // Create SVG path
      const path = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "path",
      );
      path.setAttribute("d", "M3.5 10.5L10.5 3.5M3.5 3.5L10.5 10.5");
      path.setAttribute("stroke", "#6C6C6C");
      path.setAttribute("stroke-linecap", "round");
      path.setAttribute("stroke-linejoin", "round");

      // Assemble the structure
      svg.appendChild(path);
      svgWrapper.appendChild(svg);
      button.appendChild(svgWrapper);
      pill.appendChild(groupSpan);
      pill.appendChild(valueSpan);
      pill.appendChild(button);

      return pill;
    }

    // Event listeners
    searchInput?.addEventListener("input", filterArticles);
    filterInputs.forEach((i) => i.addEventListener("change", filterArticles));

    // Pill button handlers
    pillButtons.forEach((btn) => {
      btn.addEventListener("click", () => {
        // Toggle active state (single-select within group)
        const wasActive = btn.classList.contains(FILTER_CLASSES.ACTIVE);
        pillButtons.forEach((b) => b.classList.remove(FILTER_CLASSES.ACTIVE));
        if (!wasActive) btn.classList.add(FILTER_CLASSES.ACTIVE);

        filterArticles();
      });
    });

    // Sort handlers
    sortSelect?.addEventListener("change", (e) => {
      const value = e.target.value;
      if (sortSelectMobile) sortSelectMobile.value = value;
      sortArticles(value);
      updateSortInUrl(value);
    });

    sortSelectMobile?.addEventListener("change", (e) => {
      const value = e.target.value;
      if (sortSelect) sortSelect.value = value;
      sortArticles(value);
      updateSortInUrl(value);
    });

    // Mobile drawer handlers
    const openBtn = document.querySelector("[data-open-filter]");
    const filterDrawer = document.querySelector("[data-filter-drawer]");
    const closeBtns = document.querySelectorAll("[data-close-filter]");
    const applyBtn = document.querySelector("[data-apply-filter]");

    openBtn?.addEventListener("click", () => {
      if (filterDrawer) {
        filterDrawer.hidden = false;
        document.documentElement.style.overflow = "hidden";
      }
    });

    function closeFilter() {
      if (filterDrawer) {
        filterDrawer.hidden = true;
        document.documentElement.style.overflow = "";
      }
    }

    closeBtns.forEach((btn) => btn.addEventListener("click", closeFilter));
    applyBtn?.addEventListener("click", () => {
      filterArticles();
      closeFilter();
    });
  });
})();
